import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function CareersPage() {
  const [showCVForm, setShowCVForm] = useState(false);
  const [showHRForm, setShowHRForm] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  const [cvFormData, setCvFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: '',
    cv: null as File | null
  });
  
  const [hrFormData, setHrFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [applicationData, setApplicationData] = useState({
    fullName: '',
    email: '',
    phone: '',
    availability: '',
    experience: '',
    coverLetter: '',
    cv: null as File | null
  });

  // Data arrays
  const values = [
    {
      title: 'Innovation',
      description: 'We embrace cutting-edge technologies and encourage creative problem-solving.',
      icon: 'ri-lightbulb-line'
    },
    {
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, delivering exceptional results.',
      icon: 'ri-star-line'
    },
    {
      title: 'Collaboration',
      description: 'We believe in the power of teamwork and diverse perspectives.',
      icon: 'ri-team-line'
    },
    {
      title: 'Growth',
      description: 'We invest in our people and foster continuous learning and development.',
      icon: 'ri-plant-line'
    }
  ];

  const departments = [
    { id: 'all', label: 'All Departments' },
    { id: 'engineering', label: 'Engineering' },
    { id: 'consulting', label: 'Consulting' },
    { id: 'security', label: 'Security' },
    { id: 'sales', label: 'Sales & Marketing' }
  ];

  const jobOpenings = [
    {
      id: '1',
      title: 'Senior Cloud Architect',
      department: 'engineering',
      location: 'Manchester, UK',
      type: 'Full-time',
      experience: '5+ years',
      salary: '£70,000 - £90,000',
      description: 'Lead cloud infrastructure design and implementation for enterprise clients.',
      requirements: ['AWS/Azure expertise', 'Kubernetes', 'Terraform', 'DevOps practices']
    },
    {
      id: '2',
      title: 'Cybersecurity Consultant',
      department: 'security',
      location: 'Manchester, UK / Remote',
      type: 'Full-time',
      experience: '3+ years',
      salary: '£55,000 - £75,000',
      description: 'Provide cybersecurity consulting and implement security solutions for clients.',
      requirements: ['Security frameworks', 'Risk assessment', 'Penetration testing', 'Compliance']
    },
    {
      id: '3',
      title: 'DevOps Engineer',
      department: 'engineering',
      location: 'Manchester, UK',
      type: 'Full-time',
      experience: '2+ years',
      salary: '£45,000 - £65,000',
      description: 'Build and maintain CI/CD pipelines and infrastructure automation.',
      requirements: ['Docker', 'Jenkins', 'Git', 'Linux', 'Scripting']
    },
    {
      id: '4',
      title: 'Business Development Manager',
      department: 'sales',
      location: 'Manchester, UK',
      type: 'Full-time',
      experience: '4+ years',
      salary: '£50,000 - £70,000 + Commission',
      description: 'Drive business growth and build relationships with enterprise clients.',
      requirements: ['B2B sales', 'Technology sector', 'Relationship building', 'Presentation skills']
    }
  ];

  const benefits = [
    {
      title: 'Competitive Salary',
      description: 'Market-leading compensation packages with annual reviews and performance bonuses.',
      icon: 'ri-money-pound-circle-line'
    },
    {
      title: 'Flexible Working',
      description: 'Hybrid working model with flexible hours to support work-life balance.',
      icon: 'ri-time-line'
    },
    {
      title: 'Learning Budget',
      description: '£5,000 annual learning budget for courses, certifications, and conferences.',
      icon: 'ri-book-open-line'
    },
    {
      title: 'Health & Wellness',
      description: 'Comprehensive health insurance, mental health support, and wellness programs.',
      icon: 'ri-heart-pulse-line'
    },
    {
      title: 'Career Growth',
      description: 'Clear career progression paths with mentorship and leadership development.',
      icon: 'ri-arrow-up-line'
    },
    {
      title: 'Team Events',
      description: 'Regular team building activities, social events, and annual company retreats.',
      icon: 'ri-calendar-event-line'
    }
  ];

  const filteredJobs = selectedDepartment === 'all' 
    ? jobOpenings 
    : jobOpenings.filter(job => job.department === selectedDepartment);

  // Event handlers
  const handleCVSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let cvFileBase64 = null;
      let cvFileName = null;

      // Convert CV file to base64 if it exists
      if (cvFormData.cv) {
        cvFileName = cvFormData.cv.name;
        const reader = new FileReader();
        cvFileBase64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const base64 = reader.result as string;
            // Remove the data:*/*;base64, prefix
            const base64Content = base64.split(',')[1];
            resolve(base64Content);
          };
          reader.onerror = reject;
          reader.readAsDataURL(cvFormData.cv!);
        });
      }

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-cv-submission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: cvFormData.name,
          email: cvFormData.email,
          phone: cvFormData.phone,
          position: cvFormData.position,
          experience: cvFormData.experience,
          message: cvFormData.message,
          cvFile: cvFileBase64,
          cvFileName: cvFileName
        }),
      });

      if (response.ok) {
        alert('CV submitted successfully! We will contact you soon.');
        setCvFormData({ name: '', email: '', phone: '', position: '', experience: '', message: '', cv: null });
        setShowCVForm(false);
      } else {
        alert('Failed to submit CV. Please try again.');
      }
    } catch (error) {
      alert('Error submitting CV. Please try again.');
    }
  };

  const handleHRSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-hr-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(hrFormData),
      });

      if (response.ok) {
        alert('Message sent successfully! HR team will respond soon.');
        setHrFormData({ name: '', email: '', subject: '', message: '' });
        setShowHRForm(false);
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('Error sending message. Please try again.');
    }
  };

  const scrollToPositions = () => {
    const el = document.getElementById('open-positions');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const openApplicationModal = (jobId: string) => {
    setSelectedJob(jobId);
    setApplicationData({
      fullName: '',
      email: '',
      phone: '',
      availability: '',
      experience: '',
      coverLetter: '',
      cv: null
    });
    setSubmitStatus('idle');
  };

  const closeApplicationModal = () => {
    setSelectedJob(null);
    setSubmitStatus('idle');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setApplicationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setApplicationData(prev => ({
      ...prev,
      cv: file
    }));
  };

  const handleApplicationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const jobTitle = jobOpenings.find(job => job.id === selectedJob)?.title || '';
      
      let cvFileBase64 = null;
      let cvFileName = null;

      // Convert CV file to base64 if it exists
      if (applicationData.cv) {
        cvFileName = applicationData.cv.name;
        const reader = new FileReader();
        cvFileBase64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const base64 = reader.result as string;
            // Remove the data:*/*;base64, prefix
            const base64Content = base64.split(',')[1];
            resolve(base64Content);
          };
          reader.onerror = reject;
          reader.readAsDataURL(applicationData.cv!);
        });
      }
      
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-job-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: applicationData.fullName,
          email: applicationData.email,
          phone: applicationData.phone,
          availability: applicationData.availability,
          experience: applicationData.experience,
          coverLetter: applicationData.coverLetter,
          cvFile: cvFileBase64,
          cvFileName: cvFileName,
          jobTitle: jobTitle,
          jobId: selectedJob
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          closeApplicationModal();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCVFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setCvFormData(prev => ({
      ...prev,
      cv: file
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-teal-600 text-white py-20">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/8b6ad63d4ab3e5f950c4212133fbe8ad.jpg')`
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Join Our Team</h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Build your career with Yellow Atlas. We're looking for passionate professionals 
            who want to shape the future of technology and make a real impact.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setShowCVForm(true)}
              className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Send Your CV
            </button>
            <button 
              onClick={() => setShowHRForm(true)}
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact HR Team
            </button>
          </div>
        </div>
      </section>

      {/* CV Submission Form Modal */}
      {showCVForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Submit Your CV</h3>
                <button 
                  onClick={() => setShowCVForm(false)}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form onSubmit={handleCVSubmit} data-readdy-form id="cv-form">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={cvFormData.name}
                      onChange={(e) => setCvFormData({...cvFormData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={cvFormData.email}
                      onChange={(e) => setCvFormData({...cvFormData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={cvFormData.phone}
                      onChange={(e) => setCvFormData({...cvFormData, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Position of Interest</label>
                    <input
                      type="text"
                      name="position"
                      value={cvFormData.position}
                      onChange={(e) => setCvFormData({...cvFormData, position: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                    <input
                      type="text"
                      name="experience"
                      value={cvFormData.experience}
                      onChange={(e) => setCvFormData({...cvFormData, experience: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      required
                    />
                  </div>

                  {/* CV Upload Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Upload Your CV *</label>
                    <div className="relative">
                      <input
                        type="file"
                        id="cv-upload"
                        name="cv"
                        accept=".pdf,.doc,.docx,.txt"
                        onChange={handleCVFileChange}
                        required
                        className="hidden"
                      />
                      <label
                        htmlFor="cv-upload"
                        className="w-full px-3 py-3 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-teal-500 transition-colors flex items-center justify-center text-gray-600 hover:text-teal-600"
                      >
                        <div className="text-center">
                          <i className="ri-upload-cloud-line text-2xl mb-2"></i>
                          <p className="text-sm font-medium">
                            {cvFormData.cv 
                              ? `Selected: ${cvFormData.cv.name}` 
                              : 'Click to upload your CV (PDF, DOC, DOCX, TXT)'
                            }
                          </p>
                          <p className="text-xs text-gray-500 mt-1">Maximum file size: 10MB</p>
                        </div>
                      </label>
                    </div>
                    {cvFormData.cv && (
                      <div className="mt-2 flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700 truncate">{cvFormData.cv.name}</span>
                        <button
                          type="button"
                          onClick={() => setCvFormData(prev => ({...prev, cv: null}))}
                          className="text-red-500 hover:text-red-700 cursor-pointer"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Message</label>
                    <textarea
                      name="message"
                      value={cvFormData.message}
                      onChange={(e) => setCvFormData({...cvFormData, message: e.target.value})}
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
                      placeholder="Tell us about yourself and why you're interested in joining our team..."
                    />
                    <div className="text-xs text-gray-500 mt-1">{cvFormData.message.length}/500 characters</div>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 bg-teal-500 hover:bg-teal-600 text-white py-2 px-4 rounded-md font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Submit CV
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCVForm(false)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* HR Contact Form Modal */}
      {showHRForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Contact HR Team</h3>
                <button 
                  onClick={() => setShowHRForm(false)}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form onSubmit={handleHRSubmit} data-readdy-form id="hr-contact-form">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={hrFormData.name}
                      onChange={(e) => setHrFormData({...hrFormData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={hrFormData.email}
                      onChange={(e) => setHrFormData({...hrFormData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={hrFormData.subject}
                      onChange={(e) => setHrFormData({...hrFormData, subject: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <textarea
                      name="message"
                      value={hrFormData.message}
                      onChange={(e) => setHrFormData({...hrFormData, message: e.target.value})}
                      rows={5}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
                      placeholder="How can our HR team help you?"
                      required
                    />
                    <div className="text-xs text-gray-500 mt-1">{hrFormData.message.length}/500 characters</div>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 bg-teal-500 hover:bg-teal-600 text-white py-2 px-4 rounded-md font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Send Message
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowHRForm(false)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 px-4 rounded-md font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Second Hero Section */}
      <section className="relative bg-gradient-to-br from-yellow-50 to-green-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Join Our <span className="text-yellow-500">Team</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Build your career with Yellow Atlas Consulting and help shape the future of technology for businesses across the UK and beyond.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={scrollToPositions}
                className="bg-yellow-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-yellow-600 transition-colors whitespace-nowrap cursor-pointer mr-4"
              >
                View Open Positions
              </button>
              <Link 
                to="/careers/culture"
                className="border-2 border-yellow-500 text-yellow-600 px-8 py-3 rounded-lg font-semibold hover:bg-yellow-50 transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                Learn About Culture
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-yellow-500 mb-2">50+</div>
              <div className="text-gray-600 font-medium">Team Members</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-green-500 mb-2">5 Years</div>
              <div className="text-gray-600 font-medium">Average Tenure</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-yellow-500 mb-2">95%</div>
              <div className="text-gray-600 font-medium">Employee Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-green-500 mb-2">£5K</div>
              <div className="text-gray-600 font-medium">Learning Budget</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These core values guide everything we do and shape our company culture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${value.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Openings */}
      <section id="open-positions" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Open Positions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover exciting career opportunities and join our growing team of technology experts.
            </p>
          </div>

          {/* Department Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {departments.map((dept) => (
              <button
                key={dept.id}
                onClick={() => setSelectedDepartment(dept.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedDepartment === dept.id
                    ? 'bg-yellow-500 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100 shadow-sm'
                }`}
              >
                {dept.label}
              </button>
            ))}
          </div>

          {/* Job Listings */}
          <div className="space-y-6">
            {filteredJobs.map((job) => (
              <div key={job.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <h3 className="text-2xl font-bold text-gray-900">{job.title}</h3>
                      <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                        {departments.find(d => d.id === job.department)?.label}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-6 mb-4 text-gray-600">
                      <div className="flex items-center">
                        <i className="ri-map-pin-line mr-2"></i>
                        {job.location}
                      </div>
                      <div className="flex items-center">
                        <i className="ri-time-line mr-2"></i>
                        {job.type}
                      </div>
                      <div className="flex items-center">
                        <i className="ri-user-line mr-2"></i>
                        {job.experience}
                      </div>
                      <div className="flex items-center">
                        <i className="ri-money-pound-circle-line mr-2"></i>
                        {job.salary}
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4">{job.description}</p>
                    
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Key Requirements:</h4>
                      <div className="flex flex-wrap gap-2">
                        {job.requirements.map((req, index) => (
                          <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                            {req}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="lg:ml-8 mt-6 lg:mt-0">
                    <button 
                      onClick={() => openApplicationModal(job.id)}
                      className="w-full lg:w-auto bg-gradient-to-r from-yellow-500 to-green-500 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                    >
                      Apply Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-900">
                  Apply for {jobOpenings.find(job => job.id === selectedJob)?.title}
                </h3>
                <button
                  onClick={closeApplicationModal}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <form id="job-application-form" onSubmit={handleApplicationSubmit} data-readdy-form className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={applicationData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={applicationData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                    placeholder="your.email@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={applicationData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                    placeholder="+44 123 456 7890"
                  />
                </div>
                <div>
                  <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-2">
                    Availability
                  </label>
                  <div className="relative">
                    <select
                      id="availability"
                      name="availability"
                      value={applicationData.availability}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm pr-8 appearance-none bg-white"
                    >
                      <option value="">Select availability</option>
                      <option value="immediate">Immediate</option>
                      <option value="2-weeks">2 weeks notice</option>
                      <option value="1-month">1 month notice</option>
                      <option value="2-months">2 months notice</option>
                      <option value="3-months">3+ months notice</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                  Relevant Experience *
                </label>
                <textarea
                  id="experience"
                  name="experience"
                  value={applicationData.experience}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm resize-vertical"
                  placeholder="Briefly describe your relevant experience and qualifications..."
                ></textarea>
              </div>

              <div className="mb-6">
                <label htmlFor="coverLetter" className="block text-sm font-medium text-gray-700 mb-2">
                  Cover Letter *
                </label>
                <textarea
                  id="coverLetter"
                  name="coverLetter"
                  value={applicationData.coverLetter}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm resize-vertical"
                  placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {applicationData.coverLetter.length}/500 characters
                </p>
              </div>

              {/* CV Upload Field */}
              <div className="mb-6">
                <label htmlFor="cv" className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Your CV *
                </label>
                <div className="relative">
                  <input
                    type="file"
                    id="cv"
                    name="cv"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileChange}
                    required
                    className="hidden"
                  />
                  <label
                    htmlFor="cv"
                    className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-yellow-500 transition-colors flex items-center justify-center text-gray-600 hover:text-yellow-600"
                  >
                    <div className="text-center">
                      <i className="ri-upload-cloud-line text-3xl mb-2"></i>
                      <p className="text-sm font-medium">
                        {applicationData.cv 
                          ? `Selected: ${applicationData.cv.name}` 
                          : 'Click to upload your CV (PDF, DOC, DOCX, TXT)'
                        }
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Maximum file size: 10MB</p>
                    </div>
                  </label>
                </div>
                {applicationData.cv && (
                  <div className="mt-2 flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm text-gray-700 truncate">{applicationData.cv.name}</span>
                    <button
                      type="button"
                      onClick={() => setApplicationData(prev => ({...prev, cv: null}))}
                      className="text-red-500 hover:text-red-700 cursor-pointer"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <Link
                  to="/careers/culture"
                  className="bg-yellow-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-yellow-600 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  Learn About Culture
                </Link>
                <button type="button" className="border-2 border-yellow-500 text-yellow-600 px-6 py-3 rounded-lg font-semibold hover:bg-yellow-50 transition-colors whitespace-nowrap cursor-pointer">
                  View Benefits
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="button"
                  onClick={closeApplicationModal}
                  className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  Thank you! Your application has been submitted successfully. We'll review it and get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  Sorry, there was an error submitting your application. Please try again or contact us directly.
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Work With Us?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We offer comprehensive benefits and a supportive work environment that helps you thrive both professionally and personally.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-green-500 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${benefit.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Culture */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Life at Yellow Atlas
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                We believe that great work comes from great people who are passionate about what they do. Our team culture is built on collaboration, innovation, and mutual respect.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Collaborative and inclusive work environment</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Regular team building activities and social events</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Open communication and feedback culture</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-500 mr-3"></i>
                  <span className="text-gray-700">Opportunities to work on cutting-edge projects</span>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Happy%20diverse%20team%20of%20IT%20professionals%20working%20together%20in%20modern%20office%20environment%2C%20collaborative%20workspace%2C%20team%20meeting%2C%20professional%20technology%20company%20culture%20with%20natural%20lighting&width=600&height=400&seq=team-culture&orientation=landscape"
                alt="Team Culture"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-yellow-500 to-green-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Don't see the perfect role? We're always looking for talented individuals to join our team. Send us your CV and let's talk about opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setShowCVForm(true)}
              className="bg-white text-yellow-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Send Your CV
            </button>
            <button 
              onClick={() => setShowHRForm(true)}
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-yellow-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact HR Team
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
